import { useRef, useState } from "react";
import { Upload, X, FileText, Image as ImageIcon, RefreshCw } from "lucide-react";
import { API } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = "image/png,image/jpeg,image/webp,image/heic,image/heif,.md,.txt,.markdown,application/pdf";

/**
 * Multi-file dropzone. Calls onChange([...uploadDescriptors]) whenever the
 * list updates. Each descriptor: {id, filename, mime, size, purpose}.
 *
 * `purpose` defaults to "seed" (used to enrich the prompt). Pass purpose="asset"
 * to bundle uploads into the exported Assets.xcassets.
 */
export default function FileDropzone({ purpose = "seed", value = [], onChange, label, hint }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    const next = [...value];
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", purpose);
        const token = localStorage.getItem("dpa_token");
        const res = await fetch(`${API}/uploads`, {
          method: "POST",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          toast.error(j.detail || `Upload failed: ${f.name}`);
          continue;
        }
        next.push(await res.json());
      }
      onChange?.(next);
    } finally {
      setBusy(false);
    }
  };

  const removeAt = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    upload(e.dataTransfer.files);
  };

  return (
    <div data-testid={`dropzone-${purpose}`}>
      {label && <div className="label-eyebrow mb-2">{label}</div>}
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer card-soft p-4 text-center border-2 border-dashed transition ${
          drag ? "border-marigold bg-marigold/5" : "border-border hover:border-ink"
        }`}
        data-testid={`dropzone-trigger-${purpose}`}
      >
        <div className="inline-flex items-center gap-2 text-ink2 text-sm">
          {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : (
            <>
              <span className="font-medium text-ink">Drop files</span> or click to browse
              {hint && <span className="text-xs">— {hint}</span>}
            </>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => upload(e.target.files)}
          data-testid={`dropzone-input-${purpose}`}
        />
      </div>

      {value.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`dropzone-list-${purpose}`}>
          {value.map((f, i) => (
            <li key={f.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg bg-paper border border-border" data-testid={`dropzone-item-${f.id}`}>
              {f.mime?.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-marigold" /> : <FileText className="w-3.5 h-3.5 text-marigold" />}
              <span className="font-mono truncate flex-1">{f.filename}</span>
              <span className="text-ink2">{Math.round((f.size || 0) / 1024)} KB</span>
              <button
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="text-ink2 hover:text-marigold"
                data-testid={`dropzone-remove-${f.id}`}
                title="Remove"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
